import { useEffect, useMemo } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

const SITE_NAME = 'NexusGo Labs';
const DEFAULT_SITE_URL = 'https://nexusgo.ai';

type SeoPayload = {
  title: string;
  description: string;
  keywords: string;
  robots?: 'index,follow' | 'noindex,nofollow';
  ogType?: 'website' | 'article';
};

const routeSeoRules: Array<{
  pattern: string;
  resolve: (searchParams: URLSearchParams) => SeoPayload;
}> = [
  {
    pattern: '/course/:courseId/study',
    resolve: () => ({
      title: '正在學習',
      description: '在 NexusGo 學習中心觀看課程、追蹤學習進度並持續精進職涯能力。',
      keywords: '課程學習,學習進度,線上學習,NexusGo',
      robots: 'noindex,nofollow'
    })
  },
  {
    pattern: '/course/:courseId',
    resolve: () => ({
      title: '課程詳情',
      description: '查看 NexusGo 精選課程內容、章節安排、導師背景與報名資訊。',
      keywords: '課程詳情,線上課程,AI課程,職涯課程,NexusGo'
    })
  },
  {
    pattern: '/mentor/:mentorId',
    resolve: () => ({
      title: '導師詳情',
      description: '查看導師背景、專業領域、諮詢服務與可預約時段，安排你的 1v1 職涯諮詢。',
      keywords: '導師詳情,職涯導師,一對一諮詢,導師預約,NexusGo'
    })
  },
  {
    pattern: '/blog/:postId',
    resolve: () => ({
      title: '文章詳情',
      description: '閱讀 NexusGo 資源中心的產業洞察、求職策略與實戰經驗分享。',
      keywords: '職涯文章,產業洞察,面試攻略,資源中心,NexusGo',
      ogType: 'article'
    })
  },
  {
    pattern: '/',
    resolve: () => ({
      title: '首頁',
      description: 'NexusGo 連結全球頂尖導師與學員，提供導師預約、課程學習與 AI 職涯成長服務。',
      keywords: 'NexusGo,AI職涯,導師平台,課程平台,職涯成長'
    })
  },
  {
    pattern: '/mentors',
    resolve: () => ({
      title: '導師列表',
      description: '瀏覽 NexusGo 精選導師，依專長、語言與職涯方向找到最適合你的顧問。',
      keywords: '導師列表,職涯導師,AI導師,海外導師,NexusGo'
    })
  },
  {
    pattern: '/courses',
    resolve: () => ({
      title: '課程列表',
      description: '探索 NexusGo 精選課程，系統化提升面試、履歷與職涯競爭力。',
      keywords: '課程列表,AI課程,面試課程,履歷課程,NexusGo'
    })
  },
  {
    pattern: '/services',
    resolve: () => ({
      title: '會員訂閱',
      description: '查看 NexusGo 會員方案與升級權益，獲得更完整的職涯服務與學習資源。',
      keywords: '會員訂閱,會員方案,職涯服務,NexusGo'
    })
  },
  {
    pattern: '/blog',
    resolve: () => ({
      title: '資源中心',
      description: '在 NexusGo 資源中心獲取職涯洞察、面試攻略、履歷優化與行業趨勢。',
      keywords: '資源中心,職涯內容,面試攻略,履歷優化,NexusGo'
    })
  },
  {
    pattern: '/contact',
    resolve: (searchParams) => {
      const tab = searchParams.get('tab') || 'partner';
      const tabTitleMap: Record<string, string> = {
        partner: '企業合作',
        mentor: '導師入駐',
        student: '學員支援'
      };
      return {
        title: `聯絡我們｜${tabTitleMap[tab] || '合作諮詢'}`,
        description: '提交企業合作、導師入駐或學員支援需求，NexusGo 團隊將儘快與你聯繫。',
        keywords: '聯絡我們,企業合作,導師入駐,學員支援,NexusGo'
      };
    }
  },
  {
    pattern: '/account',
    resolve: () => ({
      title: '個人中心',
      description: '管理你的課程、訂單、預約與個人資料，掌握完整學習與職涯進度。',
      keywords: '個人中心,我的課程,課程訂單,我的預約,NexusGo',
      robots: 'noindex,nofollow'
    })
  },
  {
    pattern: '/legal',
    resolve: (searchParams) => {
      const tab = searchParams.get('tab') || 'privacy';
      const tabTitleMap: Record<string, string> = {
        privacy: '隱私政策',
        terms: '服務條款',
        refund: '退款政策',
        cookie: 'Cookie 設定'
      };
      return {
        title: `法律文件｜${tabTitleMap[tab] || '法律與隱私'}`,
        description: '查看 NexusGo 隱私政策、服務條款、退款政策與 Cookie 設定。',
        keywords: '隱私政策,服務條款,退款政策,Cookie,NexusGo'
      };
    }
  },
  {
    pattern: '/login',
    resolve: () => ({
      title: '登入',
      description: '登入 NexusGo，進入你的個人中心、課程學習與導師預約服務。',
      keywords: '登入,NexusGo帳號,會員登入',
      robots: 'noindex,nofollow'
    })
  },
  {
    pattern: '/register',
    resolve: () => ({
      title: '註冊',
      description: '註冊 NexusGo 帳號，開始你的課程學習與國際職涯成長旅程。',
      keywords: '註冊,NexusGo會員,建立帳號',
      robots: 'noindex,nofollow'
    })
  },
  {
    pattern: '*',
    resolve: () => ({
      title: '頁面不存在',
      description: '你訪問的頁面不存在，請返回 NexusGo 首頁繼續瀏覽。',
      keywords: '404,頁面不存在,NexusGo',
      robots: 'noindex,nofollow'
    })
  }
];

const upsertMeta = (key: string, value: string, attr: 'name' | 'property' = 'name') => {
  if (!value) return;
  let element = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attr, key);
    document.head.appendChild(element);
  }
  element.setAttribute('content', value);
};

const upsertCanonical = (href: string) => {
  let element = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
};

const RouteSeo = () => {
  const location = useLocation();

  const seo = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const matchedRule = routeSeoRules.find((rule) => matchPath({ path: rule.pattern, end: true }, location.pathname));
    return (matchedRule || routeSeoRules[routeSeoRules.length - 1]).resolve(searchParams);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const siteUrl = (import.meta.env.VITE_SITE_URL || DEFAULT_SITE_URL).replace(/\/$/, '');
    const canonical = `${siteUrl}${location.pathname}${location.search}`;
    const title = `${seo.title} | ${SITE_NAME}`;

    document.title = title;
    document.documentElement.lang = 'zh-Hant';

    upsertMeta('description', seo.description);
    upsertMeta('keywords', seo.keywords);
    upsertMeta('robots', seo.robots || 'index,follow');

    upsertMeta('og:title', title, 'property');
    upsertMeta('og:description', seo.description, 'property');
    upsertMeta('og:type', seo.ogType || 'website', 'property');
    upsertMeta('og:url', canonical, 'property');
    upsertMeta('og:site_name', SITE_NAME, 'property');
    upsertMeta('og:locale', 'zh_TW', 'property');
    upsertMeta('og:image', `${siteUrl}/icon.png`, 'property');

    upsertMeta('twitter:card', 'summary_large_image');
    upsertMeta('twitter:title', title);
    upsertMeta('twitter:description', seo.description);
    upsertMeta('twitter:image', `${siteUrl}/icon.png`);

    upsertCanonical(canonical);
  }, [location.pathname, location.search, seo]);

  return null;
};

export default RouteSeo;
